<template>
  <div>
    <!-- Header Section -->
    <v-row align="center" no-gutters>
      <v-col>
        <div class="font-weight-bold text-h6">{{ notification.name }}</div>
        <div class="text-body-2 grey--text">{{ notification.description }}</div>
        <div class="mt-1">
          Currently
          <strong>
            {{
              notification.NotificationSetting
                ? notification.NotificationSetting.metadata.phoneNumbers.length
                : 0
            }}
          </strong>
          phone number(s) set.
        </div>
      </v-col>

      <v-spacer></v-spacer>

      <v-col cols="auto" class="d-flex">
        <v-btn
          v-if="notification.NotificationSetting"
          color="primary"
          class="v-btn-with-radius mr-2"
          dark
          @click="open"
        >
          Edit
        </v-btn>
        <v-btn
          v-if="!notification.NotificationSetting"
          color="primary"
          class="v-btn-with-radius mr-2"
          @click="open"
          >Enable
        </v-btn>
        <v-btn
          v-if="notification.NotificationSetting"
          color="error"
          class="v-btn-with-radius"
          @click="disableNotification(notification)"
          >Disable
        </v-btn>
      </v-col>
    </v-row>

    <!-- Dialog -->
    <v-dialog v-model="dialog" max-width="720px" persistent>
      <v-card class="outlined rounded-border">
        <CardToolbar title="Text Alerts" :isModal="true" @cancel="cancel" />

        <v-card-text>
          <v-container>
            <v-row>
              <!-- Phone Numbers -->
              <v-col cols="12" md="6">
                <div class="mb-2">
                  <div class="font-weight-medium">Phone Number</div>
                  <small class="grey--text">Limit: 2 numbers</small>
                </div>
                <v-row align="center" dense>
                  <v-col>
                    <v-text-field
                      outlined
                      dense
                      clearable
                      label="Enter phone number"
                      v-model="salesAlertDialog.phoneNumber"
                      :error-messages="phoneNumberErrors"
                    />
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      class="mb-5"
                      @click="addSalesAlertPhoneNumber"
                      :disabled="
                        $v.salesAlertDialog.$invalid ||
                        salesAlert.phoneNumbers.length > 1
                      "
                    >
                      Add
                    </v-btn>
                  </v-col>
                </v-row>

                <v-list v-if="salesAlert.phoneNumbers.length" dense>
                  <v-list-item
                    v-for="(number, index) in salesAlert.phoneNumbers"
                    :key="number"
                  >
                    <v-list-item-content>{{
                      number | phone
                    }}</v-list-item-content>
                    <v-list-item-action>
                      <v-btn icon @click.stop.prevent="deletePhoneNo(index)">
                        <v-icon color="error">mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-col>

              <!-- Time Selection -->
              <v-col cols="12" md="6">
                <div class="mb-2">
                  <div class="font-weight-medium">{{ $t("labels.time") }}</div>
                  <small class="grey--text">Limit: 2 times</small>
                </div>

                <v-row align="center" dense>
                  <v-col>
                    <timePicker v-model="selectedTime" />
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      class="v-btn-with-radius"
                      @click="addSalesAlertTime"
                      :disabled="
                        !disabledTimeButton || salesAlert.times.length >= 2
                      "
                    >
                      Add
                    </v-btn>
                  </v-col>
                </v-row>

                <v-list v-if="salesAlert.times.length" dense>
                  <v-list-item
                    v-for="(time, index) in salesAlert.times"
                    :key="time.label"
                  >
                    <v-list-item-content>{{ time.label }}</v-list-item-content>
                    <v-list-item-action>
                      <v-btn icon @click.stop.prevent="deleteTime(index)">
                        <v-icon color="error">mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>

            <!-- Options -->
            <v-divider
              class="my-4"
              v-if="salesAlert.phoneNumbers.length"
            ></v-divider>

            <v-row v-if="salesAlert.phoneNumbers.length" align="center">
              <v-col cols="12" md="9">
                <span class="font-weight-medium">Message</span>
                <p class="text-body-2 grey--text">
                  Select values you'd like to receive via text.
                </p>
              </v-col>
              <v-col cols="12" md="3" class="text-right">
                <v-btn
                  color="warning"
                  dark
                  class="v-btn-with-radius"
                  @click="editAlert = true"
                >
                  Options
                </v-btn>
              </v-col>
            </v-row>

            <v-row
              v-if="salesAlert.phoneNumbers.length"
              align="center"
              class="mt-3"
            >
              <v-col cols="12" md="9">
                <span class="font-weight-medium">{{ $t("labels.shift") }}</span>
                <p class="text-body-2 grey--text">
                  {{ $t("subTitle.shiftClosed") }}
                </p>
              </v-col>
              <v-col cols="12" md="3" class="text-right">
                <v-switch
                  v-model="salesAlert.shiftClosed"
                  color="primary"
                  inset
                  :label="salesAlert.shiftClosed ? 'Enabled' : 'Disabled'"
                />
              </v-col>
            </v-row>

            <v-row
              v-if="salesAlert.phoneNumbers.length"
              align="center"
              class="mt-3"
            >
              <v-col cols="12" md="9">
                <span class="font-weight-medium">{{
                  $t("labels.invoiceDeleteAlert")
                }}</span>
                <p class="text-body-2 grey--text">
                  {{ $t("subTitle.deleteInvoiceAlert") }}
                </p>
              </v-col>
              <v-col cols="12" md="3" class="text-right">
                <v-switch
                  v-model="salesAlert.invoiceDeleteAlert"
                  color="primary"
                  inset
                  :label="
                    salesAlert.invoiceDeleteAlert ? 'Enabled' : 'Disabled'
                  "
                />
              </v-col>
            </v-row>

            <v-row
              v-if="salesAlert.phoneNumbers.length"
              align="center"
              class="mt-3"
            >
              <v-col cols="12" md="9">
                <span class="font-weight-medium">Store Open Alert</span>
                <p class="text-body-2 grey--text">
                  Send a text when the location opens (Electron login only).
                </p>
              </v-col>
              <v-col cols="12" md="3" class="text-right">
                <v-switch
                  v-model="salesAlert.storeOpenAlert"
                  color="primary"
                  inset
                  :label="salesAlert.storeOpenAlert ? 'Enabled' : 'Disabled'"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <CardActions
          @submit="submit"
          :loading="submitLoading"
          :disabled="!salesAlert.phoneNumbers.length"
          @cancel="cancel"
        />
      </v-card>
    </v-dialog>

    <!-- Options Dialog -->
    <v-dialog v-model="editAlert" max-width="500px">
      <v-card class="outlined rounded-border">
        <CardToolbar
          title="Message Options"
          :isModal="true"
          @cancel="editAlert = false"
        />
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <p>Selected Fields: {{ selectedCount }} / 8</p>
              <v-checkbox
                v-for="field in fields"
                :key="field.value"
                v-model="salesAlert.selectedFields"
                :label="field.label"
                :value="field.value"
                :disabled="isFieldDisabled(field.value)"
                hide-details
                dense
                color="primary"
              />
            </v-col>
            <v-col cols="12" md="6">
              <div v-if="selectedCount > 0">
                <p><strong>Currently Selected:</strong></p>
                <ul>
                  <li v-for="label in salesAlert.selectedFields" :key="label">
                    {{ label }}
                  </li>
                </ul>
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-btn text color="primary" @click="editAlert = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="generateObject">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import locationService from "@/modules/Location/service";
import timePicker from "@/components/Controls/TimePicker.vue";

const { validationMixin } = require("vuelidate");
const { required, minLength } = require("vuelidate/lib/validators");

export default {
  props: ["notification", "LocationId"],
  mixins: [validationMixin],
  components: { timePicker },

  data() {
    return {
      // 🔹 Dialogs
      dialog: false,
      editAlert: false,

      // 🔹 Loading and temporary state
      submitLoading: false,
      selectedTime: null,

      // 🔹 Form data
      salesAlertDialog: {
        phoneNumber: "",
        time: null,
      },

      // 🔹 Available field options
      fields: [
        { label: "Customer Count", value: "customerCount" },
        { label: "Average Invoice", value: "averageInvoice" },
        { label: "Revenue", value: "revenue" },
        { label: "Income", value: "income" },
        { label: "Card Sale", value: "cardSale" },
        { label: "Other Payment", value: "otherPayment" },
        { label: "Cash Sale", value: "cashSale" },
        { label: "Discount", value: "discounts" },
        { label: "Cash Expense", value: "expense" },
        { label: "Open Invoice", value: "openInvoice" },
        { label: "Surcharge Collected", value: "surchargeCollected" },
        { label: "Tax Collected", value: "taxCollected" },
        { label: "Cash In Hand", value: "cashInHand" },
      ],

      // 🔹 Selected alert configuration
      salesAlert: {
        phoneNumbers: [],
        times: [],
        service: "twilio",
        shiftClosed: false,
        invoiceDeleteAlert: false,
        storeOpenAlert: false,
        selectedFields: [],
      },

      enableNotification: {},
    };
  },

  computed: {
    selectedCount() {
      return this.salesAlert.selectedFields.length;
    },
    disabledTimeButton() {
      const t = this.selectedTime;
      return t && t.hh && t.mm && t.A;
    },
    phoneNumberErrors() {
      const errors = [];
      if (!this.$v.salesAlertDialog.phoneNumber.$dirty) return errors;
      if (!this.$v.salesAlertDialog.phoneNumber.required)
        errors.push("Field is required");
      if (!this.$v.salesAlertDialog.phoneNumber.minLength)
        errors.push(this.$t("validations.validLetters10"));
      return errors;
    },
  },

  validations: {
    salesAlertDialog: {
      phoneNumber: { required, minLength: minLength(10) },
    },
  },

  methods: {
    // 🔹 Dialog open/reset
    cancel() {
      this.reset();
      this.dialog = false;
    },
    open() {
      this.dialog = true;
      this.enableNotification.NotificationSetting = {
        NotificationId: this.notification.id,
        metadata: {},
        ...this.notification.NotificationSetting,
      };

      if (this.notification.NotificationSetting) {
        const meta = this.notification.NotificationSetting.metadata || {};
        this.salesAlert.phoneNumbers = JSON.parse(
          JSON.stringify(meta.phoneNumbers || [])
        );
        this.salesAlert.times = JSON.parse(JSON.stringify(meta.times || []));
        this.salesAlert.shiftClosed = meta.shiftClosed || false;
        this.salesAlert.invoiceDeleteAlert = meta.invoiceDeleteAlert || false;
        this.salesAlert.storeOpenAlert = meta.storeOpenAlert || false;
        this.salesAlert.selectedFields = meta.selectedFields
          ? JSON.parse(JSON.stringify(meta.selectedFields))
          : ["revenue", "income", "cardSale", "otherPayment", "cashSale"];
      }
    },

    // 🔹 Add / Delete Phone Numbers
    addSalesAlertPhoneNumber() {
      const num = this.salesAlertDialog.phoneNumber;
      if (!num) {
        this.$v.$touch();
        if (this.$v.$invalid) {
          this.toast("error", this.$t("toastMessage.text.missingFields"));
        }
        return;
      }
      if (this.salesAlert.phoneNumbers.includes(num)) {
        this.toast(
          "error",
          this.$t("toastMessage.text.phoneNumberAlreadyExist")
        );
        return;
      }
      this.salesAlert.phoneNumbers.push(num);
      this.salesAlertDialog.phoneNumber = "";
    },
    async deletePhoneNo(index) {
      const confirm = await this.confirm(
        this.$t("messages.text.youWontBeAbleToRevert")
      );
      if (confirm) {
        this.salesAlert.phoneNumbers.splice(index, 1);
        this.toast("success", this.$t("messages.text.phoneNumberDeleted"));
      }
    },

    // 🔹 Add / Delete Times
    convertTime(hh, mm, a) {
      let hours = Number(hh);
      const minutes = Number(mm);
      if (a === "PM" && hours < 12) hours += 12;
      if (a === "AM" && hours === 12) hours = 0;
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    },
    addSalesAlertTime() {
      const t = this.selectedTime;
      const timezone = this.$moment.tz.guess();
      const time = this.convertTime(t.hh, t.mm, t.A);
      const isDuplicate = this.salesAlert.times.some(
        (i) => i.time === time && i.timezone === timezone
      );
      if (isDuplicate) {
        this.$swal.fire({
          icon: "warning",
          title: "Duplicate Time",
          text: "This time is already added.",
        });
        return;
      }
      this.salesAlert.times.push({
        time,
        timezone,
        label: this.$moment(`2019-01-01T${time}:00`).format("hh:mm a"),
      });
      this.selectedTime = null;
    },
    async deleteTime(index) {
      const confirm = await this.confirm(
        this.$t("messages.text.youWontBeAbleToRevert")
      );
      if (confirm) this.salesAlert.times.splice(index, 1);
    },

    // 🔹 Utility helpers
    reset() {
      this.salesAlertDialog.phoneNumber = "";
      this.enableNotification = {};
      this.$v.$reset();
    },
    toast(icon, title) {
      this.$swal
        .mixin({
          toast: true,
          showConfirmButton: false,
          position: "top-end",
          timer: 3000,
        })
        .fire({ icon, title });
    },
    confirm(text) {
      return this.$swal
        .fire({
          title: this.$t("messages.title.areYouSure"),
          text,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#D32F2F",
          cancelButtonColor: "#21BED5",
          confirmButtonText: this.$t("buttons.yesDeleteIt"),
        })
        .then((r) => r.value);
    },

    // 🔹 Save selected fields
    isFieldDisabled(value) {
      return (
        this.selectedCount >= 8 &&
        !this.salesAlert.selectedFields.includes(value)
      );
    },
    generateObject() {
      this.editAlert = false;
    },

    // 🔹 Submit form
    async submit() {
      if (!this.salesAlert.phoneNumbers.length) {
        this.toast("error", "Please add phone to list.");
        return;
      }

      this.enableNotification.NotificationSetting = {
        ...this.enableNotification.NotificationSetting,
        metadata: this.salesAlert,
      };

      this.submitLoading = true;
      await locationService.Settings.Notifications.set(
        this.LocationId,
        this.enableNotification.NotificationSetting
      );
      this.submitLoading = false;

      this.toast("success", this.$t("toastMessage.text.updated"));
      this.cancel();
      this.$emit("reload");
    },

    // 🔹 Disable entire notification
    async disableNotification() {
      const confirm = await this.confirm(
        this.$t("messages.text.disableSaleAlertNotification")
      );
      if (confirm) {
        await locationService.Settings.Notifications.remove(
          this.LocationId,
          this.notification.NotificationSetting.id
        );
        this.$emit("reload");
        this.toast("success", this.$t("messages.title.disabled"));
      }
    },
  },
};
</script>
